import {
	createFileRoute,
	stripSearchParams,
	useNavigate,
	useRouteContext,
	useSearch,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { cursorToOffset, offsetToCursor } from "graphql-relay";
import { useEffect, useState, useTransition } from "react";
import { graphql, useLazyLoadQuery, useRefetchableFragment } from "react-relay";
import z from "zod";
import { BadgesTable } from "@/components/tables/badges";
import { type BadgeToEdit, EditBadgeDialog } from "@/dialogs/badge-edit-dialog";
import type { emblemasPageFragment$key } from "./__generated__/emblemasPageFragment.graphql";
import type { emblemasQuery } from "./__generated__/emblemasQuery.graphql";
import type { emblemasTableQuery } from "./__generated__/emblemasTableQuery.graphql";

const defaultValues = {
	page: 1,
} as const;

export const Route = createFileRoute(
	"/_logged/c/$club/(sidebar)/(admins)/audiencia/emblemas",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			page: z.number().min(1).default(1),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const { club } = useRouteContext({ from: "/_logged/c/$club" });
	const { page } = useSearch({ from: Route.id });
	const [isPending, startTransition] = useTransition();
	const navigate = useNavigate({
		from: Route.fullPath,
	});
	const [editDialogOpen, setEditDialogOpen] = useState(false);
	const [badgeToEdit, setBadgeToEdit] = useState<BadgeToEdit | null>(null);
	const data = useLazyLoadQuery<emblemasQuery>(
		graphql`
			query emblemasQuery($slug: String!, $first: Int, $last: Int, $after: String, $before: String) {
				club(slug: $slug) {
					memberCount
					...emblemasPageFragment @arguments(first: $first, last: $last, after: $after, before: $before)
				}
			}
		`,
		{
			slug: club?.slug,
			first: 15,
			after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
		},
	);

	const [badges, refetch] = useRefetchableFragment<
		emblemasTableQuery,
		emblemasPageFragment$key
	>(
		graphql`
			fragment emblemasPageFragment on Club
				@refetchable(queryName: "emblemasTableQuery")
				@argumentDefinitions(
					first: { type: "Int" }
					last: { type: "Int" }
					after: { type: "String" }
					before: { type: "String" }
				) {
				badges(first: $first, after: $after, before: $before, last: $last) {
					edges {
						node {
    					id
              rawID
              text
              textColor
              bgColor
              memberCount
              createdAt
              updatedAt
              createdBy {
                displayName
                avatarFallback
                photo {
                    signedUrl
                }
              }
              ...memberBadgeFragment
						}
						cursor
					}
					pageInfo {
						hasNextPage
						hasPreviousPage
						totalCount
						startCursor
						endCursor
					}
				}
			}
		`,
		data.club,
	);

	useEffect(() => {
		startTransition(() => {
			refetch(
				{
					first: 15,
					after: page === 1 ? undefined : offsetToCursor((page - 1) * 15 - 1),
				},
				{ fetchPolicy: "store-or-network" },
			);
		});
	}, [page, refetch]);

	const handleEditBadge = (badge: BadgeToEdit) => {
		setBadgeToEdit(badge);
		setEditDialogOpen(true);
	};
	const refreshBadges = () => {
		startTransition(() => {
			refetch({}, { fetchPolicy: "network-only" });
		});
	};

	return (
		<div className="p-6">
			<BadgesTable
				data={badges.badges.edges.map(({ node }) => node)}
				clubSlug={club?.slug ?? ""}
				clubMemberCount={data.club?.memberCount || 1}
				isPending={isPending}
				totalCount={badges?.badges.pageInfo.totalCount || 0}
				hasNextPage={Boolean(badges?.badges.pageInfo.hasNextPage)}
				hasPreviousPage={Boolean(badges?.badges.pageInfo.hasPreviousPage)}
				loadNext={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page + 1 }) });
					})
				}
				loadPrevious={() =>
					startTransition(() => {
						navigate({ search: (s) => ({ ...s, page: s.page - 1 }) });
					})
				}
				startIndex={
					badges?.badges.edges[0]
						? cursorToOffset(badges?.badges.edges[0].cursor) + 1
						: 1
				}
				endIndex={
					badges?.badges.edges[badges?.badges.edges.length - 1]
						? cursorToOffset(
								badges?.badges.edges[badges?.badges.edges.length - 1].cursor,
							) + 1
						: 1
				}
				onEditBadge={handleEditBadge}
				onDeleted={refreshBadges}
			/>

			<EditBadgeDialog
				badge={badgeToEdit}
				open={editDialogOpen}
				onClose={() => {
					setEditDialogOpen(false);
					setBadgeToEdit(null);
				}}
				onUpdated={refreshBadges}
			/>
		</div>
	);
}
